"use client";

import { Check, Pencil, Plus } from "lucide-react";
import { Button } from "@repo/ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui";
import { cn } from "@/lib/utils";
import { ReactNode, Ref, useEffect, useState } from "react";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { EntityInstance, PaginationData } from "@/services/entity-instance.service";
import { useInView } from "@/hooks/ui-helpers/use-in-view";
import {
  highlightedStyles,
  OptionType,
} from "./entity-instance-table/connect-or-disconect-relations/relation-instance-select";
import { useEntityInstanceLayoutStore } from "@/stores/entity-instance-field-store";
import { EntityViewComponentMapping } from "@/services/entity-view-components.service";
import { ViewComponentRenderer } from "./relation-display-components/view-component-renderer";

const isSelected = (id: string, selectedInstances: Array<EntityInstance>) => {
  return selectedInstances.some((x) => x.id === id);
};
interface RelationOptionDialogProps {
  name: string;
  isSingle: boolean;
  options: UseInfiniteQueryResult<
    InfiniteData<
      {
        entityInstances: unknown[];
        pagination: PaginationData;
      },
      unknown
    >,
    Error
  >;
  selectedInstances: Array<EntityInstance>;
  selectedOption?: OptionType;
  onSelect?: (id: string) => void;
  onUnselect?: (id: string) => void;
  isSubDialogOpen?: boolean;
  selectedSubDialogId?: string;
  entityName: string;
  searchValue: string;
  setSearchValue: (searchValue: string) => void;

  onOptionClick?: (id: string) => void;
  selectedEntityInstance?: string;
  onCreateClick?: () => void;
  relationFieldToDisplay?: string;
  /** TODO: IMPLEMENT ME */
  mapping?: EntityViewComponentMapping;
  isTightlyCoupled?: boolean;
  /** custom popover trigger; when set it replaces the default select button */
  trigger?: ReactNode;
}
export const RelationOptionDialog: React.FC<RelationOptionDialogProps> = ({
  name,
  options,
  isSingle,
  selectedInstances,
  onSelect,
  onUnselect,
  isSubDialogOpen,
  selectedSubDialogId,
  entityName,
  searchValue,
  setSearchValue,
  onOptionClick,
  selectedEntityInstance,
  onCreateClick,
  relationFieldToDisplay,
  mapping,
  selectedOption,
  isTightlyCoupled,
  trigger,
}) => {
  // TODO: this component does not render the correct component like the list does; we need to implement this later
  const [open, setOpen] = useState(false);
  const {
    state: { field },
  } = useEntityInstanceLayoutStore(entityName);

  // Use the custom field if specified, otherwise use the default field
  const displayField = relationFieldToDisplay || field;

  const handleInView = () => {
    if (!options.isLoading && !options.isFetching && options.hasNextPage && !searchValue) {
      options.fetchNextPage();
    }
  };

  const valueSelected = !!selectedEntityInstance;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ? (
          trigger
        ) : !!mapping && valueSelected ? (
          <div className="w-full">
            <ViewComponentRenderer
              mapping={mapping}
              id={selectedEntityInstance}
              option={selectedOption!}
              entityName={entityName}
              relationFieldToDisplay={relationFieldToDisplay}
              onOptionClick={onOptionClick}
              onDelete={() => setOpen(true)}
            />
          </div>
        ) : (
          <Button
            variant={"outline"}
            role="combobox"
            type="button"
            className={cn("group w-full justify-between", {
              [highlightedStyles]: isSubDialogOpen && !selectedSubDialogId,
            })}
            asChild
          >
            <div className="flex w-full items-center overflow-hidden">
              <button
                type="button"
                className={cn(
                  "flex-1 truncate text-start font-normal text-muted-foreground",
                  valueSelected && isSingle && "cursor-pointer hover:underline",
                )}
              >
                {String(name)}
              </button>

              {isSingle && valueSelected && (
                <Button
                  variant={"ghost"}
                  className="-mr-4 hover:bg-transparent"
                  size={"icon"}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!valueSelected || !isSingle) {
                      setOpen((prev) => !prev);
                      return;
                    }
                    if (!selectedEntityInstance) {
                      return;
                    }
                    onOptionClick?.(selectedEntityInstance);
                  }}
                >
                  <Pencil
                    size={14}
                    className={cn("hidden cursor-pointer text-muted-foreground group-hover:block")}
                  />
                </Button>
              )}
            </div>
          </Button>
        )}
      </PopoverTrigger>

      {/* popover to connect / diconnect new entity instances */}
      <PopoverContent className="p-0" noPortal align="start" side="right">
        <Command shouldFilter={false}>
          {!isTightlyCoupled && (
            <CommandInput
              placeholder="Search..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
          )}
          <CommandList>
            {!isTightlyCoupled && (
              <CommandEmpty>{options.isFetching ? "Loading..." : "No entries found."}</CommandEmpty>
            )}
            <CommandGroup>
              {onCreateClick && (
                <CommandItem
                  key={"create"}
                  value={"create"}
                  className="cursor-pointer text-muted-foreground"
                  onSelect={() => {
                    setOpen(false);
                    onCreateClick();
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create new {entityName}
                </CommandItem>
              )}
              {!isTightlyCoupled &&
                options.data?.pages.flatMap((page) =>
                  page.entityInstances.map((o) => {
                    const option = o as OptionType;
                    const selected = isSelected(option.id, selectedInstances);
                    return (
                      <CommandItem
                        className="cursor-pointer"
                        key={option.id}
                        value={option.id}
                        onSelect={() => {
                          if (selected) onUnselect?.(option.id);
                          else onSelect?.(option.id);
                          if (isSingle) {
                            setOpen(false);
                          }
                        }}
                      >
                        <Check
                          className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")}
                        />
                        [{option.integer_id}] {String(option?.[displayField] || "")}
                      </CommandItem>
                    );
                  }),
                )}
            </CommandGroup>
            {open && <InViewTrigger inView={handleInView} />}
            {options.isFetchingNextPage && <p className="text-center text-xs">Fetching more...</p>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface InViewTriggerProps {
  inView: () => void;
}
const InViewTrigger: React.FC<InViewTriggerProps> = ({ inView }) => {
  const [ref, isInView] = useInView({
    threshold: 0,
  });
  useEffect(() => {
    if (isInView) {
      inView();
    }
  }, [isInView, inView]);
  return <div ref={ref as Ref<HTMLDivElement>}></div>;
};
